import type { FriendShopOffer, FriendShopUser } from "interfaces/friend-shop";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectPopUpManagerV2State } from "redux/slices/pop-up-manager-v2";
import { setTriggerOnCache, selectDipState } from "redux/slices";
import useRewardsById from "hooks/useRewardsById";
import { useGoogleAnalyticsContext } from "contexts";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLinks } from "hooks/useLinks";
import { friendShopAPI } from "redux/apis/friendShop";
import {
	Container,
	CustomButton,
	DataContainer,
	ItemImage,
	Items,
	Offer,
	OfferItem,
	OffersContainer,
	RewardAmount,
	RewardAmountContainer,
	RewardItem,
	RewardsInfo,
	SubTitle,
	Title,
	TitlesContainer
} from "./styles";
import { useImagePath } from "hooks/useImagePath";
import { friendCenterAssets } from "assets/cdn";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { sortBundleRewards } from "helpers/rewardSort";

interface Props {}

interface CollectPopupPayload {
	selectedUser?: FriendShopUser;
	selectedGift?: FriendShopOffer;
	transactionId?: string;
}

export const FriendShopCollectPopup: React.FC<Props> = () => {
	const { getImage } = useImagePath("cdn");
	const { close } = usePopUpsV2();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { friend_center } = useLinks();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { stack } = useAppSelector(selectPopUpManagerV2State);
	const { data: dipData } = useAppSelector(selectDipState);
	const { data: giftsData } =
		friendShopAPI.endpoints.getGiftsFriendShop.useQueryState(undefined);
	const popup = [...stack]
		.reverse()
		.find((item) => item.variant === "friend-shop-collect-popup");
	const payload = (popup?.payload ?? {}) as CollectPopupPayload;
	const selectedGift = payload.selectedGift;
	const transactionId = payload.transactionId;
	const rewardItems = useRewardsById(selectedGift?.gifterBonusRewardId || "");
	const orderedRewardItems = sortBundleRewards(rewardItems);

	const scale = useCalculateScale(343 + 40, 465 + 40);

	const analyticsPayload = {
		event_name: "friend_shop_purchase_complete_dialog",
		event_type: "friend_shop_purchase_complete_dialog",
		source: "friend_shop",
		offer_id: selectedGift?.id,
		reward_id: selectedGift?.gifterBonusRewardId,
		transaction_id: transactionId
	};

	useEffect(() => {
		reportPlaygamiEvent("tp.web_frontend", {
			...analyticsPayload,
			action: "show"
		});
	}, []);

	const onClose = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			...analyticsPayload,
			action: "close"
		});

		const hasMatchingOffer = (giftsData?.shopOffers ?? []).some((shopOffer) => {
			if (!selectedGift) {
				return false;
			}

			return (
				shopOffer.id === selectedGift.id &&
				shopOffer.offer.id === selectedGift.offer.id &&
				shopOffer.offer.price === selectedGift.offer.price &&
				shopOffer.offer.rewardId === selectedGift.offer.rewardId &&
				shopOffer.gifterBonusRewardId === selectedGift.gifterBonusRewardId
			);
		});
		const trigger =
			dipData?.dips?.find(
				(d) => d.dip.triggerType === "purchaseSuccess-multi-reward"
			)?.dip.triggerType ?? "purchaseSuccess";

		dispatch(setTriggerOnCache(trigger));

		close({ variant: "friend-shop-collect-popup" });

		if (!hasMatchingOffer) {
			navigate(`${friend_center.path}/shop/gifts`);
		}
	};

	return (
		<Container
			scale={scale}
			$backgroundImage={getImage(
				friendCenterAssets.friendShop.collectPopupBackground
			)}
		>
			<CloseIconButton onClose={onClose} />
			<TitlesContainer>
				<Title>Your WEB gift is on its way!</Title>
				<RewardsInfo>
					Loyalty Points added to your balance. Other reward items are waiting
					in your in-game inbox.
				</RewardsInfo>
			</TitlesContainer>

			<DataContainer>
				<OffersContainer>
					<Offer>
						<SubTitle>Your reward:</SubTitle>
						<OfferItem>
							<Items>
								{orderedRewardItems.map(({ imageUrl, description }, key) => {
									return (
										<RewardItem className="imgWrap" key={key} width={"25%"}>
											<ItemImage src={imageUrl} alt="" />
											<RewardAmountContainer>
												<RewardAmount>{description}</RewardAmount>
											</RewardAmountContainer>
										</RewardItem>
									);
								})}
							</Items>
						</OfferItem>
					</Offer>
				</OffersContainer>
				<CustomButton
					variant="contained"
					themeColor="secondary"
					size="large"
					onClick={() => {
						reportPlaygamiEvent("tp.web_frontend", {
							...analyticsPayload,
							action: "click_to_collect"
						});
						onClose();
					}}
				>
					Continue Shopping
				</CustomButton>
			</DataContainer>
		</Container>
	);
};
